import type { RxDatabase } from 'rxdb';

import { createRxDatabase, addRxPlugin } from 'rxdb';
import { RxDBMigrationPlugin } from 'rxdb/plugins/migration';
import { RxDBEncryptionPlugin } from 'rxdb/plugins/encryption';
import { RxDBLeaderElectionPlugin } from 'rxdb/plugins/leader-election';
import { getRxStorageDexie } from 'rxdb/plugins/dexie';

import { collectionSiteSettings } from './schemas/collectionSiteSettings';
import { localcollections } from './schemas/localcollections';
import { applicationSettings } from './schemas/applicationSettings';
import { userData } from './schemas/userData';
import { tempCredentials } from './schemas/credentials';
import { remotecollections } from './schemas/remotecollections';
import { printlogs } from './schemas/printlogs';

import { getCurrentTimestamp, getDateTimestamp, isDateAfter, defaultDateFormats, getCurrentDateString, getTwoWeeksAgo, serializeDateField } from '@/services/date';
import { getDatabaseName } from '@/services/localDatabase';
import { getCollection, getCollections, getRecentCollections } from '@/services/webservices';
import { CURRENT_COLLECTION_SITE_KEY, getStorageItem } from '@/stores/localstorage';
import { syncStatusService } from '@/stores/xstate/sync/syncStatusMachine';

addRxPlugin( RxDBMigrationPlugin );
addRxPlugin( RxDBEncryptionPlugin );
addRxPlugin( RxDBLeaderElectionPlugin );

let rxDb: RxDatabase;

async function createDatabase() {
	rxDb = await createRxDatabase({
		name: getDatabaseName( 'occf_collections' ),
		storage: getRxStorageDexie(),
		multiInstance: true,
		password: process.env.NEXT_PUBLIC_RXDB_KEY
	});
}

export async function initialize() {
	if ( !rxDb ) {
		await createDatabase();
	}

	if ( !rxDb.applicationsettings ) {
		const collections = await rxDb.addCollections({
			collectionsitesettings: collectionSiteSettings,
			applicationsettings: applicationSettings,
			userdata: userData,
			localcollections: localcollections,
			remotecollections: remotecollections,
			tmpstorecreds: tempCredentials,
			printlogs: printlogs,
		});

		const collectionSiteId = getStorageItem( CURRENT_COLLECTION_SITE_KEY );
		const collectionSiteIdNumber = collectionSiteId ? Number( collectionSiteId ) : '';

		// check for error records
		const errors = await rxDb.collections.localcollections.find({
			selector: {
				hasError: { $eq: true },
				collectionSiteId: collectionSiteIdNumber
			}
		}).exec();

		if ( errors ) {
			errors.forEach( ( document: any ) => {
				const jsonRecord = document.toJSON();

				syncStatusService.send({ type: 'ADD_ERROR', message: {
					collectionId: jsonRecord.collectionId,
					error: jsonRecord.errorMessage
				}});
			})
		}
	}

	return rxDb;
}

function mapCollectionForStorage( collection: any, consents: any[] ) {
	const currentSyncTimestamp = getCurrentTimestamp();

	let collectionObject = collection;

	const matchedConsent = consents.find( consent => consent.collectionId === collection.id );

	if ( matchedConsent ) {
		collectionObject = {
			...collectionObject,
			collectionConsent: matchedConsent
		};
	}

	return {
		collectionId: collection.id,
		collectionObject,
		collectionSiteId: collection.collectionSiteId,
		isSynced: true,
		collectionDate: getDateTimestamp( collection.collectionDate ),
		updateDate: currentSyncTimestamp,
		status: collection.status,
		createdById: collection.createdById
	};
}

export async function searchRemoteCollections({
	startDate,
	endDate,
	searchTerm,
	showCollectionsForAllUsers
}: {
	startDate: string | null;
	endDate: string | null;
	searchTerm: string,
	showCollectionsForAllUsers: boolean
}) {
	const db = await initialize();

	const remotecollections = db.remotecollections;

	const remoteDocuments = await getCollections({
		startDate: startDate || null,
		endDate: endDate || null,
		page: 1,
		pageSize: 999,
		searchTerm: searchTerm || '',
		showCompleted: true,
		isIncludeCollectionConsent: true,
		showCollectionsForAllUsers: showCollectionsForAllUsers || false
	});

	const mappedCollections = ( remoteDocuments?.collections || [] ).map( ( collection: any ) => {
		return mapCollectionForStorage( collection, remoteDocuments.collectionConsents );
	});

	await remotecollections?.bulkUpsert( mappedCollections );
}

export async function checkLocalSpecimenIds( collectionId: string, specimenId: string ) {
	const db = await initialize();

	const localcollections = db.localcollections;

	const allLocalCollections = await localcollections?.find({}).exec();

	return allLocalCollections.some( collectionDoc => {
		const jsonDoc = collectionDoc.toJSON();

		return jsonDoc?.collectionObject?.specimenId === specimenId && jsonDoc?.collectionObject?.id !== collectionId;
	});
}

export async function fetchCollections( shouldPrune = false ) {
	let remotecollections;
	let localcollections;
	let collectionsitesettings;

	const db = await initialize();

	remotecollections = db.remotecollections;
	localcollections = db.localcollections;
	collectionsitesettings = db.collectionsitesettings;

	// set all staged items as synced
	// [TODO]: Confirm whether or not we need this; I don't think we do.
	const stagedCollections = await localcollections?.find({
		selector: { isStaged: { $eq: true } }
	}).exec();

	if ( stagedCollections ) {
		await Promise.all( stagedCollections.map( async ( localDocument: any ) => {
			await localDocument.atomicPatch({
				isSynced: true,
				isStaged: false
			});
		}));
	}

	try {
		const collectionSiteId = getStorageItem( CURRENT_COLLECTION_SITE_KEY );
		const currentSyncTimestamp = getCurrentTimestamp();
		let lastSyncedDatestring = '';

		const collectionSiteDetails = await collectionsitesettings.findOne({
			selector: {
				collectionSiteId: { $eq: collectionSiteId }
			}
		}).exec();

		if ( collectionSiteDetails ) {
			const collectionSiteJson = await collectionSiteDetails.toJSON();

			lastSyncedDatestring = collectionSiteJson.lastUpdateTime;
		}

		let remoteCollections: any;

		if ( !lastSyncedDatestring ) {
			const twoWeeksAgo = serializeDateField( getTwoWeeksAgo() );

			remoteCollections = await getRecentCollections({
				lastSyncedDate: twoWeeksAgo,
				startDate: null,
				endDate: null,
				page: 1,
				pageSize: 999,
				searchTerm: '',
				showCompleted: true,
				isIncludeCollectionConsent: true,
				showCollectionsForAllUsers: true
			});

			if ( collectionSiteDetails ) {
				await collectionSiteDetails.atomicPatch({
					lastUpdateTime: serializeDateField( getCurrentDateString(), defaultDateFormats.input )
				});
			}
		} else {
			remoteCollections = await getRecentCollections({
				lastSyncedDate: lastSyncedDatestring || '',
				startDate: null,
				endDate: null,
				page: 1,
				pageSize: 999,
				searchTerm: '',
				showCompleted: true,
				isIncludeCollectionConsent: true,
				showCollectionsForAllUsers: true
			});

			if ( collectionSiteDetails ) {
				await collectionSiteDetails.atomicPatch({
					lastUpdateTime: serializeDateField( getCurrentDateString(), defaultDateFormats.input )
				});
			}
		}

		const mappedCollections = remoteCollections.collections.map( ( collection: any ) => {
			return mapCollectionForStorage( collection, remoteCollections.collectionConsents );
		});
	
		await remotecollections?.bulkUpsert( mappedCollections );

		if ( shouldPrune ) {
			await pruneCollections();
		}
	} catch ( e: any ) {
		throw new Error( e );
	}
}

export async function clearSyncedCollections( remotecollections: any ) {
	const itemsToDelete = await remotecollections.find({}).exec();

	if ( itemsToDelete.length === 0 ) {
		return;
	}

	const idArray = itemsToDelete.map( ( collection: any ) => {
		const parsedDocument = collection.toJSON();

		return parsedDocument.collectionId;
	});

	await remotecollections.bulkRemove( idArray );
}

async function validateLocalCollections() {
	const db = await initialize();

	const remoteCollectionsDb = db.remotecollections;
	const localCollectionsDb = db.localcollections;

	// find local collections that have already been synced and remove them
	// this prevents issues with a collector creating a collection and then
	// another collector updating/finalizing it
	const localCollections = await localCollectionsDb?.find( {} ).exec();

	for ( const localCollection of localCollections ) {
		const localCollectionJson = await localCollection.toJSON();

		// check API for matching collection
		const serverCollection = await getCollection( localCollectionJson.collectionId );
	
		if ( !serverCollection ) {
			continue;
		}
		
		// if the server version is finalized, use that one and delete the local one
		// we don't need to update the remote collection record since it will be downloaded
		// if it is in the recent collections
		if ( serverCollection.status == 2 ) {
			await localCollection.remove();

			continue;
		}

		// if not, we need to see which was updated more recently
		// use the most recent one and delete the other
		const serverUpdateDate = serverCollection.updatedOn;
		const localUpdateDate = localCollectionJson.collectionObject.updatedOn;

		if ( localUpdateDate && isDateAfter( localUpdateDate, serverUpdateDate ) ) {
			// if the local version is more recent, check if there's already a remote collection
			// record. if so, delete it.
			const matchedRemoteCollection = await remoteCollectionsDb?.find({
				selector: {
					collectionId: localCollection.id
				}
			});

			matchedRemoteCollection.exec();
			matchedRemoteCollection.remove();
		} else {
			// otherwise, we should delete the local version. If the server version needs to be
			// download, it will be fetched on the dashboard.
			await localCollection.remove();
		}
	}
}

export async function pruneCollections() {
	const db = await initialize();

	const remoteCollectionsDb = db.remotecollections;

	// find old remote collections and remove them
	const twoWeeksAgo = getTwoWeeksAgo().valueOf() / 1000;

	const oldCollections = await remoteCollectionsDb?.find({
		selector: {
			collectionDate: {
				$lt: twoWeeksAgo
			},
			status: 2
		}
	});

	await oldCollections.exec();
	await oldCollections.remove();

	await validateLocalCollections();
}
